<template>
    <div class="wrapper w-100 h-100 d-flex justify-center align-center">
        <v-card class="register-card" elevation="6" rounded="xl">
            <v-card-text class="pa-8 pa-sm-10">
                <!-- Logo -->
                <div class="text-center mb-4">
                    <router-link class="d-inline-flex align-center text-decoration-none" to="/">
                        <v-img :src="logoFull" :width="180" draggable="false"/>
                        <span class="font-weight-bold font-italic text-h5 text-primary ms-1">Go</span>
                    </router-link>
                    <p class="text-caption text-medium-emphasis font-italic mt-1 mb-0">Be in touch</p>
                </div>

                <!-- Error alerts -->
                <v-alert v-if="errors.length" class="mb-3" color="error" density="compact"
                         rounded="lg" variant="tonal" closable close-label="Close Alert">
                    <p v-for="error in errors" class="mb-0">{{ error }}</p>
                    <p v-if="signInToAccept" class="mb-0 mt-2">
                        <router-link class="text-primary font-weight-bold" :to="{name: 'login', query: {redirect: '/members'}}">Sign in</router-link>
                        and accept it under Invitations on the Members page.
                    </p>
                </v-alert>

                <!-- Invitation Flow -->
                <template v-if="invitation">
                    <h2 v-if="invitation?.user === false" class="text-h5 font-weight-bold text-center mb-4">
                        Create your account
                    </h2>
                    <h2 v-else-if="invitation?.company" class="text-h5 font-weight-bold text-center mb-4">
                        Join <span class="text-primary font-italic">{{ invitation?.company }}</span>
                    </h2>
                    <v-form ref="form" v-model="formData.valid" validate-on="input lazy"
                            @submit.prevent="acceptInvitation">
                        <div v-if="invitation?.user === false">
                            <v-text-field v-model="formData['password']" :rules="[vRules.required, vRules.minLength(8)]"
                                          autocomplete="new-password" color="primary" density="comfortable" label="Password"
                                          prepend-inner-icon="mdi-lock-outline" rounded="lg"
                                          type="password" variant="outlined"/>
                            <v-text-field v-model="formData['password_confirmation']" :rules="[vRules.required]"
                                          autocomplete="new-password" color="primary" density="comfortable" label="Confirm Password"
                                          prepend-inner-icon="mdi-lock-check-outline" rounded="lg"
                                          type="password" variant="outlined"/>
                            <v-btn :disabled="!formData.valid" :loading="processing" block color="primary"
                                   rounded="lg" size="large" type="submit">Register</v-btn>
                        </div>
                        <v-btn v-else-if="invitation?.user" :loading="processing" block color="primary"
                               rounded="lg" size="large" type="submit">Accept Invitation</v-btn>
                    </v-form>
                </template>

                <!-- Self-Service Registration Flow (step wizard) -->
                <template v-else>
                    <!-- Step Indicator (hidden on the final photo step) -->
                    <step-indicator v-if="currentStep <= 5" :current-step="currentStep" :total-steps="5"
                                    :labels="['Email', 'Verify', 'Birthday', 'Password', 'Details']"/>

                    <v-divider class="my-4"/>

                    <div class="step-content" style="max-height: 58vh; overflow-y: auto">
                        <!-- STEP 1: Email (fails fast on an already-registered address) -->
                        <template v-if="currentStep === 1">
                            <h2 class="text-h6 font-weight-bold mb-1">What's your email?</h2>
                            <p class="text-body-2 text-medium-emphasis mb-4">
                                We'll send a verification code here to finish setting up.
                            </p>
                            <v-form @submit.prevent="nextFromEmail">
                                <v-text-field v-model="personalData['email']" :rules="[vRules.required, vRules.email]"
                                              color="primary" density="comfortable" label="Email" autofocus
                                              autocomplete="email" prepend-inner-icon="mdi-email-outline"
                                              rounded="lg" type="email" variant="outlined"/>
                                <v-btn :loading="checkingEmail" :disabled="checkingEmail" block color="primary" class="mt-2"
                                       rounded="lg" size="large" type="submit">Continue</v-btn>
                            </v-form>
                        </template>

                        <!-- STEP 2: Verify email (before collecting anything else) -->
                        <template v-else-if="currentStep === 2">
                            <div class="text-center mb-4">
                                <div class="verify-icon-circle mx-auto mb-3">
                                    <v-icon size="36" color="primary">mdi-email-outline</v-icon>
                                </div>
                                <h3 class="text-h6 font-weight-bold mb-1">Verify your email</h3>
                                <p class="text-body-2 text-medium-emphasis">
                                    We sent a 6-digit code to <strong>{{ personalData.email }}</strong>
                                </p>
                            </div>
                            <v-form @submit.prevent="verifyEmailCode">
                                <v-text-field v-model="personalData['code']" label="Verification Code"
                                              color="primary" density="comfortable" class="mb-2" maxlength="6"
                                              inputmode="numeric" autocomplete="one-time-code"
                                              placeholder="Enter code from your email"
                                              prepend-inner-icon="mdi-shield-key-outline"
                                              rounded="lg" variant="outlined"/>
                                <div class="d-flex mt-1" style="gap: 8px">
                                    <v-btn variant="text" rounded="lg" size="large" @click="back">Back</v-btn>
                                    <v-btn :loading="verifyingEmail" :disabled="verifyingEmail" block color="primary"
                                           rounded="lg" size="large" type="submit">Verify email</v-btn>
                                </div>
                                <div class="text-center mt-3">
                                    <span v-if="resendCooldown > 0" class="text-caption text-medium-emphasis">
                                        Resend code in {{ resendCooldown }}s
                                    </span>
                                    <a v-else class="text-caption text-primary text-decoration-none font-weight-bold cursor-pointer"
                                       @click="resendEmailCode">Resend code</a>
                                </div>
                            </v-form>
                        </template>

                        <!-- STEP 3: Birthday (COPPA age gate) -->
                        <template v-else-if="currentStep === 3">
                            <h2 class="text-h6 font-weight-bold mb-1">When's your birthday?</h2>
                            <p class="text-body-2 text-medium-emphasis mb-4">
                                Used to keep under-18 accounts safe. We never share it.
                            </p>
                            <v-form @submit.prevent="nextFromDob">
                                <v-text-field v-model="personalData['date_of_birth']" :max="today" :min="minDob"
                                              color="primary" density="comfortable" label="Date of birth"
                                              prepend-inner-icon="mdi-cake-variant-outline"
                                              rounded="lg" type="date" variant="outlined"/>
                                <div class="d-flex mt-2" style="gap: 8px">
                                    <v-btn variant="text" rounded="lg" size="large" @click="back">Back</v-btn>
                                    <v-btn block color="primary" rounded="lg" size="large" type="submit">Continue</v-btn>
                                </div>
                            </v-form>
                        </template>

                        <!-- STEP 4: Password -->
                        <template v-else-if="currentStep === 4">
                            <h2 class="text-h6 font-weight-bold mb-1">Create a password</h2>
                            <p class="text-body-2 text-medium-emphasis mb-4">
                                At least 8 characters. Longer is stronger.
                            </p>
                            <v-form @submit.prevent="nextFromPassword">
                                <v-text-field v-model="personalData['password']" :rules="[vRules.required]"
                                              autocomplete="new-password" color="primary" density="comfortable" label="Password"
                                              prepend-inner-icon="mdi-lock-outline"
                                              rounded="lg" type="password" variant="outlined"/>
                                <v-text-field v-model="personalData['password_confirmation']" :rules="[vRules.required]"
                                              autocomplete="new-password" color="primary" density="comfortable" label="Confirm password"
                                              prepend-inner-icon="mdi-lock-check-outline"
                                              rounded="lg" type="password" variant="outlined"/>
                                <div class="d-flex mt-2" style="gap: 8px">
                                    <v-btn variant="text" rounded="lg" size="large" @click="back">Back</v-btn>
                                    <v-btn block color="primary" rounded="lg" size="large" type="submit">Continue</v-btn>
                                </div>
                            </v-form>
                        </template>

                        <!-- STEP 5: Name + Address -->
                        <template v-else-if="currentStep === 5">
                            <h2 class="text-h6 font-weight-bold mb-1">Almost there</h2>
                            <p class="text-body-2 text-medium-emphasis mb-4">Tell us your name and address to finish.</p>

                            <v-form v-model="personalData.valid" validate-on="input lazy" @submit.prevent="registerUser">
                                <v-row dense>
                                    <v-col cols="6">
                                        <v-text-field v-model="personalData['first_name']" :rules="[vRules.required]"
                                                      color="primary" density="compact" label="First Name"
                                                      autocomplete="given-name"
                                                      rounded="lg" variant="outlined" autofocus/>
                                    </v-col>
                                    <v-col cols="6">
                                        <v-text-field v-model="personalData['last_name']" :rules="[vRules.required]"
                                                      color="primary" density="compact" label="Last Name"
                                                      autocomplete="family-name"
                                                      rounded="lg" variant="outlined"/>
                                    </v-col>
                                </v-row>

                                <!-- Address Section. OPTIONAL: the app was
                                     rejected for requiring a postal address to
                                     register, so none of these carries a
                                     required rule and registerUser() no longer
                                     checks them. -->
                                <div class="text-body-2 font-weight-bold mt-2 mb-2 d-flex align-center">
                                    <v-icon size="16" class="mr-1" color="primary">mdi-map-marker-outline</v-icon>
                                    Address
                                    <span class="text-medium-emphasis font-weight-regular ml-2">(optional)</span>
                                </div>
                                <v-text-field v-model="personalData['addr_1']"
                                              color="primary" density="compact" label="Street Address (optional)"
                                              autocomplete="address-line1"
                                              rounded="lg" variant="outlined"/>
                                <v-row dense>
                                    <v-col cols="6">
                                        <v-text-field v-model="personalData['addr_city']"
                                                      color="primary" density="compact" label="City"
                                                      autocomplete="address-level2"
                                                      rounded="lg" variant="outlined"/>
                                    </v-col>
                                    <v-col cols="6">
                                        <v-text-field v-model="personalData['addr_district']"
                                                      color="primary" density="compact" label="District"
                                                      rounded="lg" variant="outlined"/>
                                    </v-col>
                                </v-row>
                                <v-row dense>
                                    <v-col cols="6">
                                        <v-text-field v-model="personalData['addr_state']"
                                                      color="primary" density="compact" label="State"
                                                      autocomplete="address-level1"
                                                      rounded="lg" variant="outlined"/>
                                    </v-col>
                                    <v-col cols="6">
                                        <v-text-field v-model="personalData['addr_postalcode']"
                                                      color="primary" density="compact" label="Zip/Postal Code"
                                                      autocomplete="postal-code"
                                                      rounded="lg" variant="outlined"/>
                                    </v-col>
                                </v-row>
                                <v-autocomplete v-model="personalData['addr_country']" :items="countries"
                                               color="primary" density="compact"
                                               label="Country" autocomplete="country-name" rounded="lg" variant="outlined"/>

                                <v-text-field :model-value="circleCode" color="primary" density="compact"
                                              label="Circle code (optional)" maxlength="6"
                                              prepend-inner-icon="mdi-account-group-outline"
                                              hint="Joining family? Enter their 6-character circle code to request access."
                                              persistent-hint rounded="lg" variant="outlined" class="mb-2"
                                              style="text-transform: uppercase"
                                              @update:model-value="onCircleCode"/>

                                <v-checkbox v-model="agreedToTerms" density="compact" hide-details
                                            color="primary" class="mt-1">
                                    <template #label>
                                        <span class="text-body-2">I agree to the Terms of Service and Privacy Policy.</span>
                                    </template>
                                </v-checkbox>

                                <div class="d-flex mt-2" style="gap: 8px">
                                    <v-btn variant="text" rounded="lg" size="large" @click="back">Back</v-btn>
                                    <v-btn :disabled="processing" :loading="processing" block color="primary"
                                           rounded="lg" size="large" type="submit">Create account</v-btn>
                                </div>
                            </v-form>
                        </template>

                        <!-- STEP 6: Profile photo -->
                        <template v-else-if="currentStep === 6">
                            <div class="text-center mb-4">
                                <h3 class="text-h6 font-weight-bold mb-1">Add a profile photo</h3>
                                <p class="text-body-2 text-medium-emphasis">
                                    A photo helps your circle recognise you. You can skip and add one later.
                                </p>
                            </div>
                            <PhotoCropper @cropped="uploadPhoto"/>
                            <v-btn :loading="uploadingPhoto" block variant="text" class="mt-4"
                                   rounded="lg" @click="finishSignup">Skip for now</v-btn>
                        </template>
                    </div>

                    <p v-if="currentStep !== 6" class="text-body-2 text-center mt-4 mb-0">
                        Already have an account?
                        <a class="text-primary text-decoration-none font-weight-bold cursor-pointer"
                           @click="navigateTo('login')">Sign in</a>
                    </p>
                </template>
            </v-card-text>
        </v-card>

        <!-- Footer -->
        <div class="login-footer text-caption text-white">
            &copy; {{ new Date().getFullYear() }} SimpliGPS Go by NextEra IoT
        </div>
    </div>
</template>

<script setup>
import {useRoute, useRouter} from "vue-router"
import {onBeforeUnmount, onMounted, reactive, ref} from 'vue'
import {send} from '@/composables/apiClient'
import logoFull from "@/assets/images/logo-full.png"
import {useAuthStore} from '@/stores/authStore'
import {useNavigation} from '@/composables/globals'
import {useSnackbarStore} from '@/stores/snackBarStore'
import StepIndicator from "@/components/auth/StepIndicator.vue"
import PhotoCropper from "@/components/PhotoCropper.vue"
import countries from "@/assets/js/countries.json"
import {ageFromDob as calendarAgeFromDob, calendarDateParts} from '@/composables/calendarDate';

const router = useRouter()
const route = useRoute()
const user = ref(null)
const errors = ref([])
const invitation = ref(null)
const processing = ref(false)
const signInToAccept = ref(false)
const checkingEmail = ref(false)
const verifyingEmail = ref(false)
const uploadingPhoto = ref(false)
const invitationToken = ref(router.currentRoute.value?.params?.token ?? null)
const {navigateTo} = useNavigation()
const snackbar = useSnackbarStore()
const currentStep = ref(1)
const agreedToTerms = ref(false)
const resendCooldown = ref(0)
let resendTimer = null
const form = ref(null)
const auth = useAuthStore()

const formData = reactive({
    valid: false,
    password: null,
    password_confirmation: null,
})

const today = new Date().toISOString().slice(0, 10);
// Lower bound for the DOB picker so a pre-1900 date can't be entered - the API
// rejects `before:today` / `after:1900-01-01`, and the type=date input only caps
// the upper bound, so without this a wild year would 422 at the final register.
const minDob = new Date(new Date().setFullYear(new Date().getFullYear() - 120))
    .toISOString().slice(0, 10);
const personalData = reactive({
    valid: false,
    p: 'personal',
    email: null,
    contact_phone: null,
    date_of_birth: null,
    first_name: null,
    last_name: null,
    password: null,
    password_confirmation: null,
    addr_1: null,
    addr_district: null,
    addr_city: null,
    addr_state: null,
    addr_postalcode: null,
    addr_country: null,
    email_verification_token: null,
})

onMounted(() => {
    if (invitationToken.value) {
        fetchInvitation();
    }
    // Reuse the phone already verified in the OTP step instead of asking again.
    if (route.query.phone) {
        personalData.contact_phone = route.query.phone;
        personalData.mobile_phone = route.query.phone;
    }
})

onBeforeUnmount(() => {
    if (resendTimer) clearInterval(resendTimer);
})

const startResendCooldown = () => {
    resendCooldown.value = 60;
    if (resendTimer) clearInterval(resendTimer);
    resendTimer = setInterval(() => {
        resendCooldown.value--;
        if (resendCooldown.value <= 0) clearInterval(resendTimer);
    }, 1000);
}

/** Collect registration metadata for analytics */
const getRegistrationMetadata = () => {
    try {
        const params = new URLSearchParams(window.location.search);
        return {
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            screen_resolution: `${window.screen.width}x${window.screen.height}`,
            utm_source: params.get('utm_source') || undefined,
            utm_medium: params.get('utm_medium') || undefined,
            utm_campaign: params.get('utm_campaign') || undefined,
            referrer: document.referrer || undefined,
        };
    } catch {
        return {};
    }
};

const getClientMetadata = () => {
    const ua = navigator.userAgent;
    let browserName = 'Unknown', browserVersion = '';
    if (ua.includes('Chrome') && !ua.includes('Edg')) {
        browserName = 'Chrome';
        browserVersion = ua.match(/Chrome\/([\d.]+)/)?.[1] || '';
    } else if (ua.includes('Edg')) {
        browserName = 'Edge';
        browserVersion = ua.match(/Edg\/([\d.]+)/)?.[1] || '';
    } else if (ua.includes('Firefox')) {
        browserName = 'Firefox';
        browserVersion = ua.match(/Firefox\/([\d.]+)/)?.[1] || '';
    } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
        browserName = 'Safari';
        browserVersion = ua.match(/Version\/([\d.]+)/)?.[1] || '';
    }
    let osName = 'Unknown', osVersion = '';
    if (ua.includes('Windows')) { osName = 'Windows'; osVersion = ua.match(/Windows NT ([\d.]+)/)?.[1] || ''; }
    else if (ua.includes('Mac OS')) { osName = 'macOS'; osVersion = ua.match(/Mac OS X ([\d_.]+)/)?.[1]?.replace(/_/g, '.') || ''; }
    else if (ua.includes('Linux')) { osName = 'Linux'; }
    else if (ua.includes('Android')) { osName = 'Android'; osVersion = ua.match(/Android ([\d.]+)/)?.[1] || ''; }
    else if (ua.includes('iPhone') || ua.includes('iPad')) { osName = 'iOS'; osVersion = ua.match(/OS ([\d_]+)/)?.[1]?.replace(/_/g, '.') || ''; }
    return {
        _browser_name: browserName,
        _browser_version: browserVersion,
        _os_name: osName,
        _os_version: osVersion,
        _screen_resolution: `${screen.width}x${screen.height}`,
    };
};

const fetchInvitation = () => {
    send('get', `get-invitation/${invitationToken.value}`, null)
        .then((response) => {
            invitation.value = response.data.data
        })
        .catch((errorResponse) => {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
        })
}

const acceptInvitation = () => {
    errors.value = [];
    signInToAccept.value = false;
    processing.value = true;
    send('post', `accept-invitation/${invitationToken.value}`, formData)
        .then((response) => {
            if (response.data?.data?.token) {
                auth.login(response.data.data)
                response.data.data?.user.email_verified_at === null
                    ? navigateTo('verify-email', {user: response.data.data.user.id})
                    : router.go('dashboard')
            } else {
                snackbar.showSnackbar('Invitation accepted successfully, Please login!');
                navigateTo('login');
            }
        })
        .catch((errorResponse) => {
            const message = errorResponse.response?.data?.message;
            if (errorResponse.response?.data?.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (message) {
                errors.value.push(message);
                // The account exists and has its own password, which this page does not ask for.
                signInToAccept.value = errorResponse.response.status === 422 && !auth.isAuthenticated() && /sign in/i.test(message);
            } else {
                snackbar.showSnackbar('Something went wrong', 'error');
            }
        })
        .finally(() => processing.value = false)
}

const circleCode = ref('');
const onCircleCode = (val) => {
    circleCode.value = (val || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
};

// Age decides the minor vs adult signup branch, so it lives in a tested module
// rather than being derived inline here. See composables/calendarDate.js for why
// a date-only value cannot be read with the local getters.
const ageFromDob = (dob) => calendarAgeFromDob(dob) ?? 0;

// Step back one screen. Offered on steps 2-5 (Verify/Birthday/Password/Details);
// the account is created at step 5, so the photo step has no Back.
const back = () => {
    errors.value = [];
    if (currentStep.value > 1) currentStep.value--;
};

const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// Step 1 -> 2. Send the email code. The endpoint is phone-gated (peeks the
// phone token) and fails fast if the address is already registered, so a taken
// email surfaces here before the code step.
const nextFromEmail = async () => {
    if (checkingEmail.value) return;
    errors.value = [];
    const email = (personalData.email || '').trim().toLowerCase();
    if (!emailRegex.test(email)) {
        errors.value.push('Please enter a valid email address.');
        return;
    }
    checkingEmail.value = true;
    try {
        await send('post', 'auth/email/send-otp', {
            email,
            mobile_phone: personalData.mobile_phone || route.query.phone,
            verification_token: route.query.verification_token,
        });
        personalData.email = email;
        personalData.code = null;
        currentStep.value = 2;
        startResendCooldown();
    } catch (errorResponse) {
        if (errorResponse.response?.data?.errors?.email) {
            errors.value.push(errorResponse.response.data.errors.email[0]);
        } else if (errorResponse.response?.data?.message) {
            errors.value.push(errorResponse.response.data.message);
        } else {
            errors.value.push('Could not send the code. Please try again.');
        }
    } finally {
        checkingEmail.value = false;
    }
};

// Step 2 -> 3. Verify the emailed code; the returned token is presented at
// register so the account is created already-verified.
const verifyEmailCode = async () => {
    if (verifyingEmail.value) return;
    errors.value = [];
    if (!personalData.code || String(personalData.code).trim().length !== 6) {
        errors.value.push('Enter the 6-digit code we sent to your email.');
        return;
    }
    verifyingEmail.value = true;
    try {
        const response = await send('post', 'auth/email/verify-otp', {
            email: personalData.email,
            code: String(personalData.code).trim(),
        });
        personalData.email_verification_token = response.data?.data?.verification_token ?? null;
        currentStep.value = 3;
    } catch (errorResponse) {
        errors.value.push(
            errorResponse.response?.data?.message
            ?? (errorResponse.response ? 'Invalid or expired code.' : 'Network error. Please try again.')
        );
    } finally {
        verifyingEmail.value = false;
    }
};

const resendEmailCode = async () => {
    if (resendCooldown.value > 0) return;
    errors.value = [];
    // Start the cooldown immediately so a rapid double-click can't fire two
    // send-otp requests while the first is still in flight.
    startResendCooldown();
    try {
        await send('post', 'auth/email/send-otp', {
            email: personalData.email,
            mobile_phone: personalData.mobile_phone || route.query.phone,
            verification_token: route.query.verification_token,
        });
        snackbar.showSnackbar('A new code was sent to your email.');
    } catch (errorResponse) {
        snackbar.showSnackbar(errorResponse.response?.data?.message ?? 'Could not resend the code.', 'error');
    }
};

// Step 3 -> 4. COPPA age gate (server enforces too).
const nextFromDob = () => {
    errors.value = [];
    if (!personalData.date_of_birth) {
        errors.value.push('Please enter your date of birth.');
        return;
    }
    // Guard the same lower bound the API enforces (after:1900-01-01) so a wild
    // year fails here rather than 422-ing at the final register call.
    // Read as a calendar date: local getters made "1900-01-01" read as 1899
    // west of UTC and rejected a valid date of birth.
    if ((calendarDateParts(personalData.date_of_birth)?.year ?? 0) < 1900) {
        errors.value.push('Please enter a valid date of birth.');
        return;
    }
    if (ageFromDob(personalData.date_of_birth) < 13) {
        errors.value.push('You must be at least 13 to create an account. Ask a parent to set up tracking for you.');
        return;
    }
    currentStep.value = 4;
};

// Step 4 -> 5. Password length + confirmation match.
const nextFromPassword = () => {
    errors.value = [];
    if (!personalData.password || personalData.password.length < 8) {
        errors.value.push('Password must be at least 8 characters.');
        return;
    }
    if (personalData.password !== personalData.password_confirmation) {
        errors.value.push("Passwords don't match");
        return;
    }
    currentStep.value = 5;
};

const registerUser = () => {
    if (processing.value) return;
    errors.value = [];
    // Validate the step-5 name fields explicitly: the form submits on Enter even
    // when the button is disabled, so without this an incomplete form would fire
    // a register-personal call and come back a 422 instead of failing here.
    //
    // ADDRESS IS DELIBERATELY ABSENT and must stay that way. The app was
    // rejected for requiring a postal address to register, and this map is the
    // only thing that ever enforced it - the Vuetify :rules on those inputs are
    // cosmetic, because personalData.valid is written and never read, and the
    // submit button gates on `processing` alone. Re-adding an address key here
    // would silently make signup unsubmittable for anyone who leaves it blank,
    // with no request sent and nothing logged.
    const requiredFields = {
        'First name': personalData.first_name,
        'Last name': personalData.last_name,
    };
    const missing = Object.keys(requiredFields).filter((k) => !String(requiredFields[k] ?? '').trim());
    if (missing.length) {
        errors.value.push('Please fill in: ' + missing.join(', ') + '.');
        return
    }
    if (!agreedToTerms.value) {
        errors.value.push('Please accept the Terms of Service and Privacy Policy to continue.');
        return
    }
    if (personalData['password'] !== personalData['password_confirmation']) {
        errors.value.push("Passwords don't match");
        return
    }
    // COPPA: under-13 can't create their own account (server enforces too).
    if (personalData['date_of_birth'] && ageFromDob(personalData['date_of_birth']) < 13) {
        errors.value.push("You must be at least 13 to create an account. Ask a parent to set up tracking for you.");
        return
    }
    processing.value = true;
    // Forward the verification_token from the phone-OTP step (if the user
    // came through /signup → /signup/verify → /register). Backend requires
    // this for personal-portal signups once the #822 conditional gate is
    // flipped to hard-required. Invitation-flow users won't have this
    // query param and the backend still accepts null there.
    const payload = {
        ...personalData,
        ...getClientMetadata(),
        ...getRegistrationMetadata(),
    };
    const verificationToken = route.query.verification_token;
    if (verificationToken) payload.verification_token = verificationToken;
    if (route.query.phone && !payload.mobile_phone) payload.mobile_phone = route.query.phone;
    // Optional: join an existing circle by its 6-character code (creates a
    // pending request the owner approves). Only sent when complete to avoid validation.
    if (circleCode.value && circleCode.value.length === 6) payload.company_code = circleCode.value;
    send('post', `register-personal`, payload)
        .then((response) => {
            if (response.data.success) {
                user.value = response.data.data?.user ?? null;
                if (user.value && user.value.email_verified_at) {
                    // Email verified up front (step 2), so the account is created
                    // already-verified. Establish the session, then the photo step.
                    autoLogin();
                } else {
                    // Rare: the email token expired before the final submit, so the
                    // account was created email-unverified. Route to sign in (which
                    // sends them to email verification) instead of letting the
                    // dashboard guard bounce an unverified session back to login.
                    snackbar.showSnackbar('Account created. Please sign in to verify your email.');
                    localStorage.setItem('registered_email', personalData.email);
                    router.replace({name: 'login'});
                }
            } else {
                errors.value.push('Could not create your account. Please try again.');
            }
        })
        .catch((errorResponse) => {
            if (errorResponse.response?.data?.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response?.data?.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
        })
        .finally(() => processing.value = false)
}

const autoLogin = () => {
    send('post', 'login', {email: personalData.email, password: personalData.password}, {
        headers: {'X-Client-Source': 'web'},
    })
        .then((loginResponse) => {
            if (loginResponse.data.success && loginResponse.data.data?.token) {
                auth.login(loginResponse.data.data);
                snackbar.showSnackbar('Account created successfully!');
                // Now authenticated - offer a profile photo before the dashboard.
                processing.value = false;
                currentStep.value = 6;
            } else {
                snackbar.showSnackbar('Registration complete! Please login.');
                localStorage.setItem('registered_email', personalData.email);
                router.replace({name: 'login'});
            }
        })
        .catch(() => {
            snackbar.showSnackbar('Registration complete! Please login.');
            localStorage.setItem('registered_email', personalData.email);
            router.replace({name: 'login'});
        })
        .finally(() => processing.value = false);
}

// Profile photo step (after auto-login, so the request is authenticated).
const uploadPhoto = (file) => {
    uploadingPhoto.value = true;
    // /users/profile (UserRequest) validates first_name, last_name and timezone
    // as required, so they must be re-sent with the image or the request 422s and
    // the avatar is silently dropped. updateProfile fills only the fields present,
    // so re-sending the just-registered values (and the same browser timezone the
    // account was created with) changes nothing but the avatar.
    send('post', 'users/profile', {
        image: file,
        first_name: personalData.first_name,
        last_name: personalData.last_name,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }, {
        headers: {'Content-Type': 'multipart/form-data'},
    })
        .then((response) => {
            if (response.data?.data) {
                auth.updateAuthUser({...auth.user, ...response.data.data});
            }
        })
        .catch(() => {
            // Non-fatal: they can add a photo later from their profile.
        })
        .finally(() => {
            uploadingPhoto.value = false;
            finishSignup();
        });
};

const finishSignup = () => {
    // router.replace so the consumed verification_token in the URL isn't kept
    // in history.
    router.replace({name: 'dashboard'});
};
</script>

<style scoped>
.wrapper {
    background: url('@/assets/images/login-bg.png') rgba(38, 35, 90, 0.65);
    background-blend-mode: multiply;
    background-size: cover;
    background-position: center;
    position: relative;
}

.register-card {
    width: 100%;
    max-width: 520px;
    margin: 16px;
}

.verify-icon-circle {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: rgba(38, 35, 90, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
}

.login-footer {
    position: absolute;
    bottom: 16px;
    opacity: 0.8;
}
</style>
